import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Hint,
  List,
  RawButton,
  Text,
  ThreeDotIcon,
  WarningIcon,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { EMPTY_FIELD_VALUE, EMPTY_LABEL_SIGN } from '@/common/constants';
import { useEllipsisActive } from '@/utils';

import { useGeneralInfoStore } from '../../PersonalInformation/GeneralInfo/stores';

import { useGetHtmlPreviewQuery, useGetSmsPreviewQuery, useResendQuery } from './queries';
import ResendHistoryList from './ResendHistoryList';
import ResendListItemEmailPreview from './ResendListItemEmailPreview';
import ResendListItemLetterPreview from './ResendListItemLetterPreview';
import ResendListItemSMSPreview from './ResendListItemSMSPreview';
import {
  IResendItem,
  IResendStatusHistory,
  ResendItemStatus,
  ResendItemStatusIndicatorColorMap,
  ResendItemStatusTitlesMap,
  ResendItemType,
} from './ResendSection.types';

interface Props extends IResendItem {
  onResendCallback?: () => void;
}

const ResendListItem: React.FC<Props> = ({
  id,
  status,
  date,
  time,
  subject,
  destination,
  type,
  view = '',
  isResendable,
  templateId,
  templateVariables,
  historyList,
  onResendCallback,
}) => {
  const [isExpanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded]);

  const currentType = useMemo(() => {
    if (type === ResendItemType.Email) {
      return 'Email';
    }
    if (type === ResendItemType.Letter) {
      return 'Letter';
    }
    if (type === ResendItemType.SMS) {
      return 'SMS/Text';
    }

    return '';
  }, [type]);
  const isEmployeeEmailVerified = useGeneralInfoStore((state) => state.state.isEmployeeEmailVerified);

  const { handleResend, isProcessing } = useResendQuery({
    id,
    onResendCallback,
    onSuccess: () => {},
  });

  const { htmlContent, isHTMLContentLoading, isHTMLContentError, onRefetchHTMLContent } = useGetHtmlPreviewQuery({
    templateId,
    enabled: isExpanded && type === ResendItemType.Email,
  });

  const { smsText, isSMSTextLoading, isSmsTextError, onRefetchSmsText } = useGetSmsPreviewQuery({
    templateId,
    enabled: isExpanded && type === ResendItemType.SMS,
  });

  const currentStatus = useMemo(() => {
    let background = 'warning';

    if (isProcessing) {
      background = ResendItemStatusIndicatorColorMap[ResendItemStatus.Processing];
    } else if (ResendItemStatusIndicatorColorMap[status]) {
      background = ResendItemStatusIndicatorColorMap[status];
    }
    let text = 'Pending';
    if (isProcessing) {
      text = ResendItemStatusTitlesMap[ResendItemStatus.Processing];
    } else if (ResendItemStatusTitlesMap[status]) {
      text = ResendItemStatusTitlesMap[status];
    }

    return (
      <Box direction="row" align="center">
        <Box width="10px" height="10px" round="50%" background={background} />
        <Text margin={{ left: 'xs' }}>{text}</Text>
      </Box>
    );
  }, [isProcessing, status]);

  const isResendNotVerified = useMemo(() => {
    return !isEmployeeEmailVerified && type === ResendItemType.Email;
  }, [isEmployeeEmailVerified, type]);

  const isDisableVerificationResend = useMemo(() => {
    return !isResendable;
  }, [isResendable]);

  const currentHistory: IResendStatusHistory[] = useMemo(() => {
    return historyList || [];
  }, [historyList]);

  const formattedAddress = useMemo(() => {
    return _.compact([destination?.addressLine1, destination?.addressLine2]).join(', ');
  }, [destination?.addressLine1, destination?.addressLine2]);
  const [isTruncatedAddress, addressRef] = useEllipsisActive();

  return (
    <List.Item testId={`item_resend-list_${id}`}>
      <Box direction="column" fill>
        <Box direction="row" align="center">
          <Box direction="row" align="center" style={{ width: '200px' }}>
            <RawButton data-testid={`resend-list_item_${id}_button`} onClick={handleToggle}>
              <Box pad="xxs" data-testid={`resend-list_item_${id}_icon`} round="button2Round">
                {isExpanded ? (
                  <ChevronUpIcon color="iconPrimary"></ChevronUpIcon>
                ) : (
                  <ChevronDownIcon color="iconPrimary"></ChevronDownIcon>
                )}
              </Box>
            </RawButton>
            <Text data-testid={`resend-list_item_${id}_date`} weight={700} margin={{ left: 's' }}>
              {date}
            </Text>
            <Text data-testid={`resend-list_item_${id}_time`} weight={400} margin={{ left: 's' }}>
              {time}
            </Text>
          </Box>

          <List.Item.Text
            flex={2}
            testId={`resend-list_item_${id}_subject`}
            weight={400}
            margin={{ left: 's' }}
          >
            {subject || EMPTY_FIELD_VALUE}
          </List.Item.Text>
          <List.Item.Text flex={2} testId={`resend-list_item_${id}_send-to`} weight={400}>
            <Box pad={{ right: 'm' }}>
              {(type === ResendItemType.Letter || type === ResendItemType.Email) && isTruncatedAddress ? (
                <Hint hintElement={<Text truncate>{destination.sentTo}</Text>}>
                  <Text textAlign="center" color="textOnColor" size="medium">
                    {destination.sentTo}
                  </Text>
                </Hint>
              ) : (
                <Text truncate ref={addressRef}>
                  {destination.sentTo}
                </Text>
              )}
            </Box>
          </List.Item.Text>
          <List.Item.Text flex={1} testId={`resend-list_item_${id}_current-type`} weight={400}>
            {currentType}
          </List.Item.Text>
          <List.Item.Text flex={1.5} testId={`resend-list_item_${id}_current-status`} weight={400}>
            {currentStatus}
          </List.Item.Text>
          <Box margin={{ left: 's' }}>
            {!isResendable ? (
              <Box>
                <ThreeDotIcon color="border" style={{ cursor: 'not-allowed' }} />
              </Box>
            ) : (
              <List.Item.Options>
                {isResendNotVerified ? (
                  <Hint
                    hintAlign="vertical"
                    hintElement={
                      <Box pad={{ horizontal: 'xs' }}>
                        <Text weight={400} size="medium" color="textDisabled">
                          Resend
                        </Text>
                      </Box>
                    }
                    marginContent="m"
                  >
                    <Text size="small" textAlign="center" color="textOnColor">
                      Resend is blocked because the email address provided by the employee has not been
                      verified.
                    </Text>
                  </Hint>
                ) : isProcessing || isDisableVerificationResend ? (
                  <Box pad={{ horizontal: 'xs' }}>
                    <Text weight={400} size="medium" color="textDisabled">
                      Resend
                    </Text>
                  </Box>
                ) : (
                  <List.Item.Options.Item testId={`resend-list_item_${id}_resend`} onClick={handleResend}>
                    Resend
                  </List.Item.Options.Item>
                )}
              </List.Item.Options>
            )}
          </Box>
        </Box>
        {isExpanded && (
          <Box margin={{ top: 'm' }} data-testId={`resend-list_item_${id}_content`}>
            <Box direction="column" round="container1Round" fill background="module">
              {type === ResendItemType.Letter && (
                <Box
                  direction="row"
                  margin={{ top: 'l', left: 'l', right: 'l' }}
                  background="canvas"
                  pad="l"
                  round="container1Round"
                  border={{ size: 'small', color: 'border2' }}
                >
                  <Box direction="column" style={{ flex: 3 }}>
                    <Text color="textSecondary" size="small" weight={500}>
                      Street Address
                    </Text>
                    <Text weight={400}>{formattedAddress || EMPTY_LABEL_SIGN}</Text>
                  </Box>

                  <Box direction="column" style={{ flex: 1 }}>
                    <Text color="textSecondary" size="small" weight={500}>
                      City
                    </Text>
                    <Text weight={400}>{destination.city || EMPTY_LABEL_SIGN}</Text>
                  </Box>
                  <Box direction="column" style={{ flex: 1 }}>
                    <Text color="textSecondary" size="small" weight={500}>
                      State
                    </Text>
                    <Text weight={400}>{destination.state || EMPTY_LABEL_SIGN}</Text>
                  </Box>
                  <Box direction="column">
                    <Text color="textSecondary" size="small" weight={500}>
                      ZIP code
                    </Text>
                    <Text weight={400}>{destination.zipCode || EMPTY_LABEL_SIGN}</Text>
                  </Box>
                </Box>
              )}

              <Box pad="l" fill align="start" direction="row">
                {isResendable ? (
                  <Box
                    direction="column"
                    fill
                    background="canvas"
                    pad="l"
                    round="container1Round"
                    border={{ size: 'small', color: 'border2' }}
                  >
                    {type === ResendItemType.Email && (
                      <ResendListItemEmailPreview
                        htmlContent={htmlContent}
                        isHTMLContentLoading={isHTMLContentLoading}
                        htmlVariables={templateVariables}
                        isHTMLContentError={isHTMLContentError}
                        onRefetchHTMLContent={onRefetchHTMLContent}
                        testId={`resend-list_item_${id}_preview`}
                      />
                    )}
                    {type === ResendItemType.SMS && (
                      <ResendListItemSMSPreview
                        smsText={smsText || ''}
                        smsVariables={templateVariables}
                        isSMSTextLoading={isSMSTextLoading}
                        isSmsTextError={isSmsTextError}
                        onRefetchSmsText={onRefetchSmsText}
                        testId={`resend-list_item_${id}_preview`}
                      />
                    )}
                    {type === ResendItemType.Letter && (
                      <ResendListItemLetterPreview src={view} testId={`resend-list_item_${id}_preview`} />
                    )}
                  </Box>
                ) : (
                  <Box
                    direction="column"
                    fill
                    background="canvas"
                    pad="l"
                    round="container1Round"
                    border={{ size: 'small', color: 'border2' }}
                  >
                    <Text size="medium" weight={700}>
                      Sent message
                    </Text>
                    <Box pad={{ top: 'm' }}>
                      <Box background="border1" pad={{ vertical: 'l', horizontal: '50px' }}>
                        <Box pad="m" round="container1Round" background="module">
                          <Box
                            pad="s"
                            align="center"
                            justify="center"
                            round="container2Round"
                            background="canvas"
                            direction="column"
                            data-testId="sensitive-communication-item"
                          >
                            <WarningIcon size="32px" />
                            <Box width={{ max: '370px' }}>
                              <Text textAlign="center" weight={400}>
                                Notification containing information to assist in participant login cannot be
                                viewed or resent by admins. The participant may create additional requests for
                                this information through the participant portal login page.
                              </Text>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                <ResendHistoryList communicationType={type} items={currentHistory}></ResendHistoryList>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </List.Item>
  );
};

export default ResendListItem;
